import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader, DemoBadge } from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Building2, Package, Boxes, Users, Layers, ShieldCheck, Plus, Pencil, Trash2,
} from "lucide-react";
import { CarrierDialog, ProductDialog } from "@/pages/admin/AdminForms";
import { OfferingsDialog } from "@/pages/admin/OfferingsDialog";

// The three Nick-reviewed founders. One-click provisioning payloads live here (no secrets);
// provisioning runs through the existing authenticated admin API (platform_admin only).
const FOUNDER_ROSTER = [
  { email: "alan@seniorbenefitadvisor.com", name: "Alan Gomberg", npn: "3478956",
    frontdoor_slug: "alan-gomberg", business_display: "Alan Gomberg", frontdoor_enabled: true,
    sunfire_purl: "https://www.sunfirematrix.com/app/consumer/yourmedicare/3478956/",
    contact_cell: "+15616570373", telnyx_area_code_pref: "561",
    enrollment_routes: [
      { fmo_code: "AMERILIFE", url: "https://www.sunfirematrix.com/app/consumer/yourmedicare/3478956/" },
      { fmo_code: "CAREPOINT", url: "https://www.sunfirematrix.com/app/consumer/ember/?sfpath=cpt&sfagid=3478956" },
    ] },
  { email: "gary@seniorbenefitadvisor.com", name: "Gary Sheets", npn: "15327078",
    frontdoor_slug: "gary-sheets", business_display: "Gary Sheets", frontdoor_enabled: true,
    sunfire_purl: "https://www.sunfirematrix.com/app/consumer/yourmedicare/15327078/",
    contact_cell: "+16789720985", telnyx_area_code_pref: "850",
    enrollment_routes: [
      { fmo_code: "AMERILIFE", url: "https://www.sunfirematrix.com/app/consumer/yourmedicare/15327078/" },
    ] },
  { email: "erik@seniorbenefitadvisor.com", name: "Erik Sheets", npn: "21207717",
    frontdoor_slug: "erik-sheets", business_display: "Erik Sheets", frontdoor_enabled: true,
    sunfire_purl: "https://www.sunfirematrix.com/app/consumer/yourmedicare/21207717/",
    contact_cell: "+12489390707", telnyx_area_code_pref: "813",
    enrollment_routes: [
      { fmo_code: "AMERILIFE", url: "https://www.sunfirematrix.com/app/consumer/yourmedicare/21207717/" },
    ] },
];

export default function AdminConsole() {
  const [stats, setStats] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [carrierDialog, setCarrierDialog] = useState({ open: false, initial: null });
  const [productDialog, setProductDialog] = useState({ open: false, initial: null });
  const [offeringsDialog, setOfferingsDialog] = useState({ open: false, employer: null });
  const [provisioning, setProvisioning] = useState(null);
  const [provisionResults, setProvisionResults] = useState({});

  const provisionFounder = async (f) => {
    setProvisioning(f.email);
    try {
      const { data } = await api.post("/admin/provision-advisor", f);
      const parts = [`account: ${data.user?.status}`, `producer: ${data.producer?.status}`];
      if (data.frontdoor?.status) parts.push(`front door: ${data.frontdoor.status}`);
      if (data.routes?.length) parts.push(`routes: ${data.routes.map((r) => `${r.fmo_code}=${r.status}`).join(", ")}`);
      if (data.conflicts?.length) parts.push(`conflicts: ${data.conflicts.join("; ")}`);
      setProvisionResults((s) => ({ ...s, [f.email]: parts.join(" · ") }));
      toast.success(`${f.name}: front door /f/${f.frontdoor_slug} ready`);
    } catch (e) {
      setProvisionResults((s) => ({ ...s, [f.email]: "error — please retry" }));
      toast.error(`Unable to provision ${f.name}`);
    } finally {
      setProvisioning(null);
    }
  };

  const load = useCallback(() => {
    return Promise.all([
      api.get("/admin/stats"), api.get("/admin/carriers"), api.get("/admin/products"),
      api.get("/admin/benefit-categories"), api.get("/admin/employers"), api.get("/admin/users"),
    ]).then(([s, c, p, bc, e, u]) => {
      setStats(s.data); setCarriers(c.data); setProducts(p.data);
      setCategories(bc.data); setEmployers(e.data); setUsers(u.data);
    });
  }, []);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  const deleteCarrier = async (id) => {
    try { await api.delete(`/admin/carriers/${id}`); toast.success("Carrier deleted"); load(); }
    catch { toast.error("Unable to delete carrier"); }
  };
  const deleteProduct = async (id) => {
    try { await api.delete(`/admin/products/${id}`); toast.success("Product deleted"); load(); }
    catch { toast.error("Unable to delete product"); }
  };

  if (loading) return <div className="p-6 sm:p-12 max-w-7xl mx-auto"><Skeleton className="h-96 w-full rounded-xl" /></div>;

  const statCards = [
    { label: "Employers", value: stats.employers, icon: Building2 },
    { label: "Users", value: stats.users, icon: Users },
    { label: "Carriers", value: stats.carriers, icon: Boxes },
    { label: "Products", value: stats.products, icon: Package },
    { label: "Categories", value: stats.benefit_categories, icon: Layers },
    { label: "Active enrollments", value: stats.active_enrollments, icon: ShieldCheck },
  ];

  return (
    <div className="p-6 sm:p-10 lg:p-12 max-w-7xl mx-auto animate-rise">
      <PageHeader
        eyebrow="Platform administration"
        title="Nauticus control center"
        subtitle="Author carriers, products, and eligibility rules. Product data is modular and ready for real carrier materials."
        action={products.some((p) => p.is_demo) ? <DemoBadge className="mt-1" /> : null}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-xl border border-mist-200 p-5" data-testid={`admin-stat-${s.label.split(" ")[0].toLowerCase()}`}>
              <Icon className="w-5 h-5 text-gold-dark mb-3" />
              <div className="font-display text-3xl font-bold text-navy-900">{s.value}</div>
              <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
            </div>
          );
        })}
      </div>

      <Tabs defaultValue="products">
        <TabsList className="bg-white border border-mist-200" data-testid="admin-tabs">
          <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="carriers" data-testid="tab-carriers">Carriers</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="employers" data-testid="tab-employers">Employers</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="founders" data-testid="tab-founders">Founders</TabsTrigger>
        </TabsList>

        {/* Products */}
        <TabsContent value="products">
          <TableCard
            title="Products"
            testid="admin-products"
            onAdd={() => setProductDialog({ open: true, initial: null })}
            addLabel="Add product"
            addTestId="add-product-button"
            head={["Product", "Carrier", "Category", "Type", "Source", ""]}
            rows={products.map((p) => ({
              id: p.id,
              cells: [p.name, p.carrier?.name || "—", p.benefit_category_key, p.product_type, p.is_demo ? "demo" : "carrier"],
              onEdit: () => setProductDialog({ open: true, initial: p }),
              onDelete: () => deleteProduct(p.id),
            }))}
          />
        </TabsContent>

        {/* Carriers */}
        <TabsContent value="carriers">
          <TableCard
            title="Carriers"
            testid="admin-carriers"
            onAdd={() => setCarrierDialog({ open: true, initial: null })}
            addLabel="Add carrier"
            addTestId="add-carrier-button"
            head={["Carrier", "Code", "Rating", ""]}
            rows={carriers.map((c) => ({
              id: c.id,
              cells: [c.name, c.code, c.am_best_rating || "—"],
              onEdit: () => setCarrierDialog({ open: true, initial: c }),
              onDelete: () => deleteCarrier(c.id),
            }))}
          />
        </TabsContent>

        <TabsContent value="categories">
          <TableCard title="Benefit categories" testid="admin-categories" head={["Category", "Key", "Description"]}
            rows={categories.map((c) => ({ id: c.id, cells: [c.name, c.key, c.short_description] }))} />
        </TabsContent>
        <TabsContent value="employers">
          <TableCard title="Employers" testid="admin-employers" head={["Employer", "Group", "Industry", "State", "Offerings", ""]}
            rows={employers.map((e) => ({
              id: e.id,
              cells: [e.name, e.group_number || "—", e.industry || "—", e.state || "—", `${(e.offered_product_ids || []).length} products`],
              onEdit: () => setOfferingsDialog({ open: true, employer: e }),
            }))} />
        </TabsContent>
        <TabsContent value="users">
          <TableCard title="Users" testid="admin-users" head={["Name", "Email", "Role"]}
            rows={users.map((u) => ({ id: u.id, cells: [u.name, u.email, (u.role || "").replace("_", " ")] }))} />
        </TabsContent>
        <TabsContent value="founders">
          <div className="bg-white rounded-xl border border-mist-200 overflow-hidden mt-4" data-testid="admin-founders">
            <div className="p-5 border-b border-mist-200">
              <h3 className="font-display text-lg font-bold text-navy-900">Founder advisors (SBA)</h3>
              <p className="text-xs text-muted-foreground mt-1 max-w-3xl">
                One click sets up each reviewed founder: creates a staged advisor account + producer,
                turns on their branded front door, and files their SunFire enrollment links (kept off
                until their states/carriers are confirmed). Safe to click more than once — it never
                duplicates or changes an existing account or password.
              </p>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Founder</TableHead><TableHead>Email</TableHead><TableHead>NPN</TableHead>
                  <TableHead>Front door</TableHead><TableHead>Last result</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {FOUNDER_ROSTER.map((f) => (
                  <TableRow key={f.email} data-testid={`founder-row-${f.frontdoor_slug}`}>
                    <TableCell className="font-semibold text-navy-900">{f.name}</TableCell>
                    <TableCell className="text-muted-foreground">{f.email}</TableCell>
                    <TableCell className="text-muted-foreground">{f.npn}</TableCell>
                    <TableCell className="text-muted-foreground">/f/{f.frontdoor_slug}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-sm">{provisionResults[f.email] || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold"
                        disabled={provisioning === f.email} onClick={() => provisionFounder(f)}
                        data-testid={`provision-${f.frontdoor_slug}`}>
                        {provisioning === f.email ? "Provisioning…" : "Provision"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <CarrierDialog
        open={carrierDialog.open}
        initial={carrierDialog.initial}
        onOpenChange={(o) => setCarrierDialog((s) => ({ ...s, open: o }))}
        onSaved={load}
      />
      <ProductDialog
        open={productDialog.open}
        initial={productDialog.initial}
        carriers={carriers}
        categories={categories}
        onOpenChange={(o) => setProductDialog((s) => ({ ...s, open: o }))}
        onSaved={load}
      />
      <OfferingsDialog
        open={offeringsDialog.open}
        employer={offeringsDialog.employer}
        products={products}
        onOpenChange={(o) => setOfferingsDialog((s) => ({ ...s, open: o }))}
        onSaved={load}
      />
    </div>
  );
}

function TableCard({ title, head, rows, testid, onAdd, addLabel, addTestId }) {
  return (
    <div className="bg-white rounded-xl border border-mist-200 overflow-hidden mt-4">
      {(title || onAdd) && (
        <div className="p-5 border-b border-mist-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-navy-900">{title}</h3>
          {onAdd && (
            <Button onClick={onAdd} className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold" size="sm" data-testid={addTestId}>
              <Plus className="w-4 h-4 mr-1" /> {addLabel}
            </Button>
          )}
        </div>
      )}
      <Table data-testid={testid}>
        <TableHeader>
          <TableRow>{head.map((h, i) => <TableHead key={i}>{h}</TableHead>)}</TableRow>
        </TableHeader>
        <TableBody>
          {rows.length ? rows.map((r) => (
            <TableRow key={r.id} data-testid={`${testid}-row-${r.id}`}>
              {r.cells.map((cell, j) => (
                <TableCell key={j} className={j === 0 ? "font-semibold text-navy-900 capitalize" : "text-muted-foreground capitalize"}>{cell}</TableCell>
              ))}
              {(r.onEdit || r.onDelete) && (
                <TableCell className="text-right whitespace-nowrap">
                  {r.onEdit && (
                    <Button variant="ghost" size="icon" onClick={r.onEdit} data-testid={`edit-${testid}-${r.id}`}>
                      <Pencil className="w-4 h-4 text-navy-700" />
                    </Button>
                  )}
                  {r.onDelete && (
                    <Button variant="ghost" size="icon" onClick={r.onDelete} data-testid={`delete-${testid}-${r.id}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          )) : <TableRow><TableCell colSpan={head.length} className="text-center text-muted-foreground py-8">No records.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}
